import { AssetEnum, AssetRepository } from "@/lib/AssetRepository";
import { AppContextEnum } from "@/lib/enum/appContext.enum";
import { ImageResizerOutputypeEnum } from "@/lib/enum/imageReziserOutputype.enum";
import { LanguageCodeEnum } from "@/lib/enum/language-code.enum";
import { IImageResizeOptions } from "@/lib/interfaces/image-resize-options.interface";
import { IAppColorConfig } from "@/lib/utility/AppColorSchema";
import { loadLocaleAsync, SUPPORT_LOCALES } from "@/plugins/I18nPlugin";
import Vuetify from "@/plugins/VuetifyPlugin";
import VuetifyThg from "@/plugins/VuetifyPluginThg";
import { MrfiktivAppColorThemeGen } from "@/services/mrfiktiv/v1/data-contracts";
import { safeGetItem, safeSetItem } from "@/lib/safeStorage";
import store from "@/store/VuexPlugin";
import { SessionModule } from "@/store/modules/session";
import { Action, Module, Mutation, MutationAction, VuexModule, getModule } from "vuex-module-decorators";
import { version } from "../../../package.json";
import { INavigationSettings } from "../interface/navigation-settings.interface";

@Module({
  dynamic: true,
  namespaced: true,
  name: "config",
  store
})
export class Config extends VuexModule {
  LOCAL_DARK_MODE = "darkMode";
  darkMode: boolean = JSON.parse(safeGetItem(this.LOCAL_DARK_MODE) || "false");

  debug = false;

  fahrzeugscheinToken = "";

  isLoginSheetVisible = false;
  isFahrzeugscheinTokenValid = false;

  BASE_URL_PORT = 3000;
  fahrzeugscheinBaseUrl = "https://api.mmmint.ai/fahrzeugschein/v1";

  privacyPolicyLink = "#/privacy/";

  throttleTimerInMs = 2000;

  developmentStatusAcknowledgement = false;

  docphantUrls = ["https://dev.app.docphant.com/#", "https://app.docphant.com/#"];

  // Backend URL
  useLocalHostMrfiktiv = (process.env.VUE_APP_LOCALHOST_MRFIKTIV ?? process.env.VUE_APP_LOCALHOST) === "true";
  useLocalHostSign = (process.env.VUE_APP_LOCALHOST_SIGN ?? process.env.VUE_APP_LOCALHOST) === "true";
  useLocalHostThg = (process.env.VUE_APP_LOCALHOST_THG ?? process.env.VUE_APP_LOCALHOST) === "true";
  useLocalHostBooking = (process.env.VUE_APP_LOCALHOST_BOOKING ?? process.env.VUE_APP_LOCALHOST) === "true";
  useLocalHostImage = (process.env.VUE_APP_LOCALHOST_IMAGE ?? process.env.VUE_APP_LOCALHOST) === "true";
  useLocalHostAinonymizer = (process.env.VUE_APP_LOCALHOST_AINONYMIZER ?? process.env.VUE_APP_LOCALHOST) === "true";

  productionUrl = "https://api.mrfiktiv.com/v1";
  developmentUrl = "https://dev.api.mrfiktiv.com/v1";
  localUrl = `http://localhost:${this.BASE_URL_PORT}`;

  productionThgUrl = "https://api.mmmint.ai/thg/v1";
  developmentThgUrl = "https://dev.api.mmmint.ai/thg/v1";

  googleThgRatingLink = "https://g.page/r/CXTEJQalPANhEAo/review";

  /** Year-specific conversion factors: Tonnes = MWh × factor */
  mwhToTonnes: Record<number, number> = {
    2025: 0.37287432,
    2026: 0.379296
  };

  productionPublicImageUrl = "https://api.mmmint.ai/image/v1";
  developmentPublicImageUrl = "https://dev.api.mmmint.ai/image/v1";

  // For development we want to use the dev key. otherwise we want to fetch it from the config endpoint.
  devAppInsightsKey = "43fa64c5-1620-4eb3-88d7-9819e4f17d47";
  prodAppInsightsKey = "05cf2f6a-8b2b-4c1b-9cab-3c67afdd0ecb";
  prodThgAppInsightsKey = "244675ed-b1f7-4da6-a496-f86fc27a1b85";
  appInsightsKey = process.env.NODE_ENV === "production" ? this.prodAppInsightsKey : this.devAppInsightsKey;
  appThgInsightsKey = process.env.NODE_ENV === "production" ? this.prodThgAppInsightsKey : this.devAppInsightsKey;

  productionPortalUrl = "https://portal.schadensmeldung.digital/#";
  developmentPortalUrl = "https://dev.portal.schadensmeldung.digital/#";
  localPortalUrl = "localhost:8080/portal#";

  portalUrl =
    process.env.NODE_ENV === "production"
      ? this.productionPortalUrl
      : this.useLocalHostMrfiktiv
        ? this.localPortalUrl
        : this.developmentPortalUrl;

  thgBaseUrl =
    process.env.NODE_ENV === "production"
      ? this.productionThgUrl
      : this.useLocalHostThg
        ? this.localUrl
        : this.developmentThgUrl;

  mrfiktivBaseUrl =
    process.env.NODE_ENV === "production"
      ? this.productionUrl
      : this.useLocalHostMrfiktiv
        ? this.localUrl
        : this.developmentUrl;

  publicImageBaseUrl =
    process.env.NODE_ENV === "production"
      ? this.productionPublicImageUrl
      : this.useLocalHostImage
        ? this.localUrl
        : this.developmentPublicImageUrl;

  bookingBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://api.mmmint.ai/booking/v1" //TODO: VERIFY URL
      : this.useLocalHostBooking
        ? this.localUrl
        : "https://dev.api.mmmint.ai/booking/v1"; //TODO: VERIFY URL

  digitalSignatureBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://api.mmmint.ai/sign/v1"
      : this.useLocalHostSign
        ? this.localUrl
        : "https://dev.api.mmmint.ai/sign/v1";

  digitalSignatureThgBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://api.mmmint.ai/sign-thg/v1"
      : this.useLocalHostSign
        ? this.localUrl
        : "https://dev.api.mmmint.ai/sign/v1";

  ainonymizerBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://api.mmmint.ai/ainonymizer/v1"
      : this.useLocalHostAinonymizer
        ? this.localUrl
        : "https://dev.api.mmmint.ai/ainonymizer/v1";

  // WebSocket Service URL
  useLocalHostWss = (process.env.VUE_APP_LOCALHOST_WSS ?? process.env.VUE_APP_LOCALHOST) === "true";
  WSS_LOCAL_PORT = 3001;
  localWssUrl = `http://localhost:${this.WSS_LOCAL_PORT}`;

  wssBaseUrl =
    process.env.NODE_ENV === "production"
      ? "https://api.mmmint.ai/"
      : this.useLocalHostWss
        ? this.localWssUrl
        : "https://dev.api.mmmint.ai/";

  wssSocketPath = this.useLocalHostWss ? "/socket.io" : "/wss/v1/socket.io";

  thgRatingUrl = "https://de.trustpilot.com/evaluate/wirkaufendeinethg.de";

  // Image Resizing
  isResizingImagesReport = true;
  reportImageResizingOptions: IImageResizeOptions = {
    maxWidth: 2560,
    maxHeight: 2560,
    outputType: ImageResizerOutputypeEnum.JPG,
    outputQuality: 0.8
  };

  registrationImageResizingOptions: IImageResizeOptions = {
    maxWidth: 2560,
    maxHeight: 2560,
    outputType: ImageResizerOutputypeEnum.JPG,
    outputQuality: 1
  };

  defaultLogoUrl = AssetRepository.getAsset(false, AssetEnum.mmmint);

  // Defaults Colors Analytics
  mintFutureColor: Required<Pick<MrfiktivAppColorThemeGen, "primary" | "secondary" | "palette">> = {
    primary: "#7bb942",
    secondary: "#BDBDBD",
    palette: ["#7bb942", "#FFCA0D", "#4e4e4e", "#2A88F5", "#CFF000", "#F5F5F5", "#e4f2ee", "#FFCA0D"]
  };

  // Default Colors APP
  mintFutureColorSchema: IAppColorConfig = {
    themes: {
      light: {
        primary: "#7bb942",
        secondary: "#2c2f3b",
        warning: "#f7bb23",
        success: "#70cd00",
        error: "#ff4040",
        info: "#56a0da"
      },
      dark: {
        primary: "#7bb942",
        secondary: "#2c2f3b",
        warning: "#f7bb23",
        success: "#70cd00",
        error: "#ff4040",
        info: "#56a0da"
      }
    }
  };

  // Defaults Colors Analytics
  damageReportColor: Required<Pick<MrfiktivAppColorThemeGen, "primary" | "secondary" | "palette">> = {
    primary: "#418EF5",
    secondary: "#3BC8C1",
    palette: ["#3BC8C1", "#418EF5", "#424242", "#9E9E9E", "#8BD6B8", "#63B2A9", "#AAEB94", "#FFCA0D"]
  };

  // Default Colors APP
  damageReportColorSchema: IAppColorConfig = {
    themes: {
      light: {
        primary: "#363844",
        info: "#888888",
        secondary: "#F0F0F0"
      },
      dark: {
        primary: "#363844",
        info: "#888888",
        secondary: "#F0F0F0"
      }
    }
  };

  portalReportColorSchema: IAppColorConfig = {
    themes: {
      light: {
        primary: "#418EF5",
        secondary: "#3BC8C1"
      },
      dark: {
        primary: "#418EF5",
        secondary: "#3BC8C1"
      }
    }
  };

  get imageInputTypes() {
    return ["image/webp", "image/png", "image/jpeg", ".jpg", ".jpeg", ".png", ".ico"];
  }

  get imageInputTypesString() {
    return this.imageInputTypes.join(",");
  }

  get pdfInputTypes() {
    return this.imageInputTypes.concat(["application/pdf", ".pdf"]);
  }

  get pdfInputTypesString() {
    return this.pdfInputTypes.join(",");
  }

  get isProd() {
    return (
      process.env.NODE_ENV === "production" || location.hostname === "localhost" || location.hostname === "127.0.0.1"
    );
  }

  get color(): Required<Pick<MrfiktivAppColorThemeGen, "primary" | "secondary" | "palette">> {
    switch (this.appContext) {
      case AppContextEnum.THG_PORTAL:
        return this.mintFutureColor;
      case AppContextEnum.REPORT_PORTAL:
        return this.damageReportColor;
      case AppContextEnum.REPORT:
        return this.damageReportColor;
      case AppContextEnum.SIGN:
        return this.damageReportColor;
      case AppContextEnum.THG:
        return this.mintFutureColor;
      default:
        return this.mintFutureColor;
    }
  }

  /**
   * The NavigationSettings
   */
  _navigationSettings: INavigationSettings = {
    showMmmmintLogo: true,
    showTheReportList: false,
    // Show links in user-facing app
    showTheBookingList: false,
    showThePartnerList: true,
    showTheCustomerList: true,
    showTheOrganizationList: true,
    showTheFleetList: true,
    showTheAnonymizer: true,
    // Shows links in portal app
    showTheOnlineBookingList: true,
    showTheAdminList: true,
    showTheThgAdminList: false,
    showTheThgList: false,
    showTheAboutList: true,
    showThgAboutList: false,
    showTheThgProcessingList: false,
    showTheThgPartnerList: false,
    showThgFeatureSettings: false,
    showDocphant: false,
    showTheDigitalSignatureList: true,
    showTheProjectList: true,
    showTheCostList: true
  };

  /**
   * Logo that is displayed in Portal
   */
  _portalLogo = AssetEnum.mmmint;

  preferredAdminThgPartner =
    process.env.NODE_ENV === "production" ? "61ffe0712754ad3b32508d26" : "61e9880bb4c41b6269ef5649";

  portalLogo() {
    return this._portalLogo;
  }

  get appVersion() {
    return version || "1.0.0";
  }

  get navigationSettings(): INavigationSettings {
    return this._navigationSettings;
  }

  /**
   * Which app is running
   */
  _appContext: AppContextEnum = AppContextEnum.REPORT;

  @Mutation
  _mutateAppContext(context: AppContextEnum) {
    this._appContext = context;
  }

  @Action
  setAppContext(context: AppContextEnum) {
    this.context.commit("_mutateAppContext", context);
  }

  /**
   * Defines if Bankinginformations are fetched
   */
  _isBankingAvailable = false;

  get isBankingAvailable() {
    return this._isBankingAvailable;
  }

  /**
   * Defines if PartnerBanking is available
   */
  _isPartnerBankingAvailable = false;

  get isPartnerBankingAvailable() {
    return this._isPartnerBankingAvailable;
  }

  /**
   * Assume the camera is available.
   */
  _isCameraAvailable = true;

  /**
   * After selecting an PArtner over the partnerselector the redirect is to the following page
   */
  _partnerSelectorDestinationVue = "PartnerDashboard";

  get isCameraAvailable() {
    return this._isCameraAvailable;
  }

  get partnerSelectorDestinationVue() {
    return this._partnerSelectorDestinationVue;
  }

  get appContext() {
    return this._appContext;
  }

  @Action
  setPartnerSelectorDestinationVue(destinationVue: string) {
    this.context.commit("_mutatePartnerSelectorDestinationVue", destinationVue);
  }

  @Mutation
  async _mutatePartnerSelectorDestinationVue(destinationVue: string) {
    this._partnerSelectorDestinationVue = destinationVue;
  }

  @Action
  setPortalLogo(logoEnum: AssetEnum) {
    this.context.commit("_mutatePortalLogo", logoEnum);
  }

  @Mutation
  async _mutatePortalLogo(logoEnum: AssetEnum) {
    this._portalLogo = logoEnum;
  }

  @Action
  setisBankingAvailable(val: boolean) {
    this.context.commit("_mutateIsBankingAvailable", val);
  }

  @Mutation
  async _mutateIsBankingAvailable(val: boolean) {
    this._isBankingAvailable = val;
  }

  @Action
  setIsPartnerBankingAvailable(val: boolean) {
    this.context.commit("_mutateIsPartnerBankingAvailable", val);
  }

  @Mutation
  async _mutateIsPartnerBankingAvailable(val: boolean) {
    this._isPartnerBankingAvailable = val;
  }

  @Action
  setNavigationSettings(navigationSettings: Partial<INavigationSettings>) {
    this.context.commit("_mutateNavigationSettings", { ...this._navigationSettings, ...navigationSettings });
  }

  @Mutation
  _mutateNavigationSettings(navigationSettings: INavigationSettings) {
    this._navigationSettings = navigationSettings;
  }

  @Action
  setIsCameraAvailable(val: boolean) {
    this.context.commit("updateIsCameraAvailable", val);
  }

  @Mutation
  async updateIsCameraAvailable(val: boolean) {
    this._isCameraAvailable = val;
  }

  @MutationAction
  async setDebug(debug: boolean) {
    return {
      debug
    };
  }

  @Action
  async setThgBaseUrl() {
    const baseUrl =
      process.env.NODE_ENV === "production"
        ? this.productionThgUrl
        : this.useLocalHostThg
          ? this.localUrl
          : this.developmentThgUrl;

    this.context.commit("setMrfiktivBaseUrl", baseUrl);
  }

  @Mutation
  async setMrfiktivBaseUrl(mrfiktivBaseUrl: string) {
    this.mrfiktivBaseUrl = mrfiktivBaseUrl;
  }

  @Mutation
  updateDevelopmentStatusAcknowledgement(val: boolean) {
    this.developmentStatusAcknowledgement = val;
  }

  @Action
  async setDevelopmentStatusAcknowledgement(val: boolean) {
    this.context.commit("updateDevelopmentStatusAcknowledgement", val);
  }

  @Action
  async setDarkMode(val: boolean) {
    safeSetItem(this.LOCAL_DARK_MODE, String(val));
    this.context.commit("updateDarkModeSetting", val);
  }

  @Mutation
  updateDarkModeSetting(val: boolean) {
    Vuetify.framework.theme.dark = val;
    VuetifyThg.framework.theme.dark = val;
    this.darkMode = val;
  }

  @Action
  async setFahrzeugscheinToken(val: string) {
    this.context.commit("updateFahrzeugscheinToken", val);
    await SessionModule.fetchSessions();
  }

  @Action
  async setFahrzeugscheinTokenValid(val: boolean) {
    this.context.commit("updateFahrzeugscheinTokenValid", val);
  }

  @Action
  async setCachedFahrzeugscheine(val: boolean) {
    this.context.commit("updateFahrzeugscheinTokenValid", val);
  }

  @Mutation
  updateFahrzeugscheinToken(val: string) {
    this.fahrzeugscheinToken = val;
  }

  @Mutation
  updateFahrzeugscheinTokenValid(val: boolean) {
    this.isFahrzeugscheinTokenValid = val;
  }

  @Action
  makeLoginSheetVisible() {
    this.context.commit("setLoginSheetVisible", true);
  }

  @Action
  hideLoginSheetVisible() {
    this.context.commit("setLoginSheetVisible", false);
  }

  @Mutation
  setLoginSheetVisible(val: boolean) {
    this.isLoginSheetVisible = val;
  }

  _supportMail = "info@mmmint.ai";

  get supportMail() {
    return this._supportMail;
  }

  @Mutation
  mutateSupportMail(supportMail: string) {
    this._supportMail = supportMail;
  }

  @Action
  setThgSupportMail() {
    const thgMail = "info@mintfuture.de";
    this.context.commit("mutateSupportMail", thgMail);
  }

  /**
   * Displays the navigation elements
   */
  _headless = false;

  get headless() {
    return this._headless;
  }

  @Mutation
  mutateHeadless(value: boolean) {
    this._headless = value;
  }

  @Action
  setHeadless(data: { value: boolean }) {
    this.context.commit("mutateHeadless", data.value);
  }

  /**
   * Languages used, e.g. available for templates
   */
  get availableLanguages() {
    return SUPPORT_LOCALES;
  }

  locale: LanguageCodeEnum | undefined = (safeGetItem("locale") as LanguageCodeEnum) ?? undefined;

  @Action
  async setLocale(val: LanguageCodeEnum) {
    await loadLocaleAsync(val);
    safeSetItem("locale", String(val));
    this.context.commit("updateLocale", val);
  }

  @Mutation
  updateLocale(val: LanguageCodeEnum) {
    this.locale = val;
  }
}

export const ConfigModule = getModule(Config);
